/**
 * The reliability diagnostic: pick any field, run it, see the gap.
 *
 * Unlike the enrichment queue, there is no fixed set of criteria here — the
 * field list itself is data (`GET /reliability/fields`), because the
 * classification levels and the attributes are this installation's own. The
 * screen therefore has two states: picking fields, and a result for the
 * fields last run. Changing the selection does not re-run anything by
 * itself — over a catalogue this size, a checkbox click should not fire a
 * query, the "Lancer" button should.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import {
  Button,
  Checkbox,
  DataTable,
  ErrorState,
  LoadingState,
  Meter,
  Page,
  useToast,
  type Column,
} from "@hardauto/core-ui";

import { CatalogApi, type ReliabilityRow, type ReliabilitySummary } from "../api";
import { NAMESPACE } from "../i18n";
import "./catalog.css";

const PAGE = 50;

/**
 * `core:reference` → `reliability.field.core_reference`.
 *
 * i18next's default key syntax treats `:` as the namespace separator, so a
 * raw field id used as a key silently fails to resolve — it is parsed as
 * "look in namespace `reliability.field.core` for key `reference`", which
 * does not exist. The underscore form is what the locale files declare.
 */
function coreFieldLabelKey(fieldId: string): string {
  return `reliability.field.${fieldId.replace(":", "_")}`;
}

export function ReliabilityScreen({ client }: { client: ApiClient }) {
  const { t } = useTranslation(NAMESPACE);
  const api = useMemo(() => new CatalogApi(client), [client]);
  const toast = useToast();
  const action = useAction();

  const fieldsFetcher = useCallback(() => api.reliabilityFields(), [api]);
  const fields = useResource(fieldsFetcher);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [ranFields, setRanFields] = useState<string[] | null>(null);
  const [summary, setSummary] = useState<ReliabilitySummary | null>(null);
  const [rows, setRows] = useState<ReliabilityRow[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const toggle = (fieldId: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(fieldId)) next.delete(fieldId);
      else next.add(fieldId);
      return next;
    });
  };

  const run = useCallback(
    (afterId?: string) => {
      const requested = [...selected];
      if (requested.length === 0) {
        toast.show(t("reliability.selectAtLeastOne"), { tone: "info" });
        return;
      }
      void action.run("run", async () => {
        try {
          const [nextSummary, page] = await Promise.all([
            // The percentage is over the whole catalogue regardless of which
            // page of the incomplete list is open, so it is only fetched for
            // a fresh run — not every "next page" click.
            afterId ? Promise.resolve(summary) : api.reliabilitySummary(requested),
            api.reliabilityArticles(requested, { afterId, limit: PAGE }),
          ]);
          setRanFields(requested);
          setSummary(nextSummary);
          setRows(page.items);
          setCursor(page.next_cursor ?? undefined);
        } catch {
          toast.show(t("error.title"), { tone: "danger" });
        }
      });
    },
    [action, api, selected, summary, t, toast],
  );

  const fieldLabel = useCallback(
    (fieldId: string) => {
      if (fieldId.startsWith("core:")) {
        return t(coreFieldLabelKey(fieldId), { defaultValue: fieldId });
      }
      const level = fields.data?.category_levels.find((entry) => entry.field_id === fieldId);
      if (level) return level.label;
      const attribute = fields.data?.attributes.find((entry) => entry.field_id === fieldId);
      return attribute?.label ?? fieldId;
    },
    [fields.data, t],
  );

  const columns: Column<ReliabilityRow>[] = [
    {
      key: "reference",
      header: t("reliability.column.reference"),
      render: (row) => <code>{row.reference}</code>,
    },
    {
      key: "designation",
      header: t("reliability.column.designation"),
      render: (row) => row.designation,
    },
    {
      key: "missing",
      header: t("reliability.column.missing"),
      render: (row) => row.missing.map((fieldId) => fieldLabel(fieldId)).join(", "),
    },
  ];

  if (fields.loading) return <LoadingState label={t("reliability.title")} />;
  if (fields.failed || !fields.data) {
    return (
      <ErrorState title={t("error.title")} onRetry={fields.reload} retryLabel={t("error.retry")} />
    );
  }

  const attributesByGroup = new Map<string, typeof fields.data.attributes>();
  for (const attribute of fields.data.attributes) {
    const bucket = attributesByGroup.get(attribute.group_label) ?? [];
    bucket.push(attribute);
    attributesByGroup.set(attribute.group_label, bucket);
  }

  return (
    <Page title={t("reliability.title")} description={t("reliability.description")}>
      <div className="hardauto-reliability__picker">
        <fieldset className="hardauto-reliability__group">
          <legend>{t("reliability.picker.core")}</legend>
          {fields.data.core.map((fieldId) => (
            <Checkbox
              key={fieldId}
              label={t(coreFieldLabelKey(fieldId), { defaultValue: fieldId })}
              checked={selected.has(fieldId)}
              onChange={() => toggle(fieldId)}
            />
          ))}
        </fieldset>

        {fields.data.category_levels.length > 0 ? (
          <fieldset className="hardauto-reliability__group">
            <legend>{t("reliability.picker.classification")}</legend>
            {fields.data.category_levels.map((level) => (
              <Checkbox
                key={level.field_id}
                label={level.label}
                checked={selected.has(level.field_id)}
                onChange={() => toggle(level.field_id)}
              />
            ))}
          </fieldset>
        ) : null}

        {[...attributesByGroup.entries()].map(([groupLabel, attributes]) => (
          <fieldset className="hardauto-reliability__group" key={groupLabel}>
            <legend>{groupLabel || t("reliability.picker.attributes")}</legend>
            {attributes.map((attribute) => (
              <Checkbox
                key={attribute.field_id}
                label={attribute.label}
                checked={selected.has(attribute.field_id)}
                onChange={() => toggle(attribute.field_id)}
              />
            ))}
          </fieldset>
        ))}
      </div>

      <Button
        variant="primary"
        busy={action.isBusy("run")}
        busyLabel={t("reliability.running")}
        onClick={() => run()}
      >
        {t("reliability.run")}
      </Button>

      {ranFields && summary ? (
        <>
          <Meter
            value={summary.completion_pct}
            label={t("reliability.summaryLabel")}
            tone={summary.completion_pct === 100 ? "success" : "accent"}
          />
          <p>{t("reliability.summary", { complete: summary.complete, total: summary.total })}</p>

          <DataTable<ReliabilityRow>
            caption={t("reliability.caption")}
            columns={columns}
            rows={rows}
            rowKey={(row) => row.reference}
            loadingLabel={t("reliability.running")}
            empty={{
              title: t("reliability.empty.title"),
              description: t("reliability.empty.body"),
            }}
          />

          {cursor ? (
            <Button busy={action.isBusy("run")} onClick={() => run(cursor)}>
              {t("reliability.next")}
            </Button>
          ) : null}
        </>
      ) : (
        <p className="hardauto-cell__muted">{t("reliability.idle.body")}</p>
      )}
    </Page>
  );
}
